import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

export type Tab = {
  id: string;
  name: string;
  disabled?: boolean;
  onClick?: () => void;
};

type UseHeaderTabsParams = {
  tabs: Tab[];
  tabActive?: string;
  onChangeTab?: (id: string) => void;
  onDisableTab: (id: string) => void;
  isURLBased: boolean;
};

function useHeaderTabs({ tabs, tabActive, onChangeTab, onDisableTab, isURLBased }: UseHeaderTabsParams) {
  const [activeTabState, setActiveTabState] = useState(tabActive);
  const [hasChangedPath, setHasChangedPath] = useState(false);
  const [tabToChangeTo, setTabToChangeTo] = useState<Tab | undefined>(tabs[0]);
  const previousTabActiveRef = useRef(tabActive);
  const tabRefs = useRef<Array<HTMLElement | null>>([]);



  const history = useHistory();

  useEffect(() => {
    return history.listen(() => {
      setHasChangedPath(true);
    });
  }, [history]);

  function onClickChangeTab(tab: Tab): void {
    if (isURLBased) {
      setTabToChangeTo(tab);
    } else if (tab.disabled) {
      onDisableTab(tab.id);
    } else {
      setActiveTabState(tab.id);
      onChangeTab?.(tab.id);
    }
    tab.onClick?.();
  }

  useEffect(() => {
    if (hasChangedPath && isURLBased && tabToChangeTo) {
      setActiveTabState(tabToChangeTo.id);
      setHasChangedPath(false);
    }
  }, [hasChangedPath, isURLBased, tabToChangeTo]);

  useEffect(() => {
    if (tabActive !== previousTabActiveRef.current && tabActive !== activeTabState) {
      setActiveTabState(tabActive);
    }
    previousTabActiveRef.current = tabActive;
  }, [tabActive, activeTabState]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>, currentIndex: number): void {
    const enabledIndexes = tabs
      .map((tab, i) => ({ tab, i }))
      .filter(({ tab }) => !tab.disabled)
      .map(({ i }) => i);

    if (enabledIndexes.length === 0) return;

    const posInEnabled = enabledIndexes.indexOf(currentIndex);
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = enabledIndexes[(posInEnabled + 1) % enabledIndexes.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = enabledIndexes[(posInEnabled - 1 + enabledIndexes.length) % enabledIndexes.length];
        break;
      case 'Home':
        event.preventDefault();
        nextIndex = enabledIndexes[0];
        break;
      case 'End':
        event.preventDefault();
        nextIndex = enabledIndexes.at(-1) ?? null;
        break;
      default:
        return;
    }

    if (nextIndex !== null) {
      tabRefs.current[nextIndex]?.focus();
      onClickChangeTab(tabs[nextIndex]);
    }
  }

  return { activeTabState, tabRefs, onClickChangeTab, handleKeyDown };
}

export default useHeaderTabs;
